import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import QuizCard from '../components/QuizCard'
import { useAuth, displayUser } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { useBlocks } from '../hooks/useBlocks'
import SEO from '../components/SEO'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

function formatNum(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

const SORT_OPTIONS = [
  { value: 'hot',     label: 'For you' },
  { value: 'newest',  label: 'Newest' },
  { value: 'popular', label: 'Most played' },
  { value: 'liked',   label: 'Most liked' },
]

export default function QuizzesPage() {
  const { user, profile } = useAuth()
  const currentUser = displayUser(user, profile)
  const blockedIds = useBlocks()

  const [quizzes, setQuizzes]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [type, setType]           = useState('')
  const [sort, setSort]           = useState('hot')
  const [search, setSearch]       = useState('')
  const [savedIds, setSavedIds]   = useState(new Set())
  const [saveBusy, setSaveBusy]   = useState(new Set())

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ sort, limit: '60' })
    if (type) params.set('type', type)
    axios.get(`/api/quizzes?${params}`)
      .then(r => setQuizzes(r.data))
      .catch(() => setQuizzes([]))
      .finally(() => setLoading(false))
  }, [type, sort])

  // Load saved quiz IDs for the current user
  useEffect(() => {
    if (!currentUser) return
    getHeaders()
      .then(headers => axios.get('/api/profile/quiz-bookmarks', { headers }))
      .then(r => setSavedIds(new Set(r.data)))
      .catch(err => console.error('[QuizzesPage] Failed to fetch saved quiz ids:', err))
  }, [currentUser?.username])

  async function handleDelete(quizId) {
    const headers = await getHeaders()
    await axios.delete(`/api/quizzes/${quizId}`, { headers })
    setQuizzes(qs => qs.filter(q => q.id !== quizId))
  }

  async function handleSaveToggle(quizId, isSaved) {
    if (saveBusy.has(quizId)) return
    setSaveBusy(s => new Set([...s, quizId]))
    try {
      const headers = await getHeaders()
      if (isSaved) {
        await axios.delete(`/api/profile/quiz-bookmarks/${quizId}`, { headers })
        setSavedIds(s => { const n = new Set(s); n.delete(quizId); return n })
      } else {
        await axios.post(`/api/profile/quiz-bookmarks/${quizId}`, {}, { headers })
        setSavedIds(s => new Set([...s, quizId]))
      }
    } catch (err) { console.error('[QuizzesPage] Failed to toggle quiz bookmark:', err) } finally {
      setSaveBusy(s => { const n = new Set(s); n.delete(quizId); return n })
    }
  }

  const filtered = quizzes
    .filter(q => !blockedIds.has(q.userId))
    .filter(q => !search.trim() ||
      q.title.toLowerCase().includes(search.toLowerCase()) ||
      q.author.toLowerCase().includes(search.toLowerCase()) ||
      q.fandom?.toLowerCase().includes(search.toLowerCase())
    )

  return (
    <div className="quizzes-page">
      <SEO title="Quizzes" description="Take personality quizzes, trivia, and knowledge tests created by the Verse community." />
      <Navbar />

      {/* Hero */}
      <div className="quizzes-hero">
        <div className="quizzes-hero__inner">
          <h1 className="quizzes-hero__title">Quizzes</h1>
          <p className="quizzes-hero__sub">Test your knowledge or discover which character you are.</p>
          {currentUser && (
            <Link to="/quiz/create" className="btn btn-primary quizzes-hero__cta">
              Create a quiz
            </Link>
          )}
        </div>
      </div>

      <div className="quizzes-body">
        {/* Filter bar */}
        <div className="quizzes-filters">
          <div className="quizzes-filters__types">
            {[['', 'All'], ['knowledge', '⚡ Knowledge'], ['personality', '🌀 Personality']].map(([v, l]) => (
              <button
                key={v}
                className={`quizzes-type-btn${type === v ? ' quizzes-type-btn--active' : ''}`}
                onClick={() => setType(v)}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="quizzes-filters__right">
            <input
              type="search"
              className="quizzes-search"
              placeholder="Search quizzes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="quizzes-sort" value={sort} onChange={e => setSort(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="quizzes-loading">Loading quizzes…</div>
        ) : filtered.length === 0 ? (
          <div className="quizzes-empty">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🧠</div>
            <p>No quizzes found yet.</p>
            {currentUser && (
              <Link to="/quiz/create" className="btn btn-primary" style={{ marginTop: 16 }}>
                Be the first to create one
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="quizzes-count">{filtered.length} quiz{filtered.length !== 1 ? 'zes' : ''}</div>
            <div className="quiz-grid">
              {filtered.map((q, i) => (
                <QuizCard
                  key={q.id}
                  quiz={q}
                  index={i}
                  saved={savedIds.has(q.id)}
                  onSaveToggle={currentUser ? handleSaveToggle : null}
                  currentUserId={user?.id ?? null}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
